import Const from 'constants';
import RuleBuilder from 'services/simplex/rule-builder';
import {getSimplexUnitName} from 'helpers/simplex-helper';
import {isPlayerOnDoublePosition} from 'helpers/player-helper';

class DoubleSecondaryStackCompatibleTypesRulesBuilder {
    constructor(dependencies) {
        this.comboIds = [];
        this.rules = [];

        const {combosMap, playersList, checkIfPlayerOnDoublePosition} = dependencies;

        this.combosMap = combosMap;
        this.playersList = playersList;
        this.checkIfPlayerOnDoublePosition = checkIfPlayerOnDoublePosition;

        this.playersQuantity = Const.CORRELATION_SECONDARY_STACK_COMBINATION_PLAYERS_QUANTITY;

        const {oneTeam, oppositeTeams} = Const.CORRELATION_STACKING_SECONDARY_COMPATIBLE_TYPES;

        this.oneTeam = oneTeam;
        this.oppositeTeams = oppositeTeams;
    }

    _isPlayerOnSinglePosition(playerId) {
        if (!this.checkIfPlayerOnDoublePosition) {
            return true;
        }

        const player = this.playersList.getPlayer(playerId);

        return !(player && isPlayerOnDoublePosition(player));
    }

    _getSimplexUnitNamesByPlayerId(playerId) {
        if (this._isPlayerOnSinglePosition(playerId)) {
            return [getSimplexUnitName(playerId)];
        }

        const player = this.playersList.getPlayer(playerId);

        if (!player) {
            return [getSimplexUnitName(playerId)];
        }

        return player.position.map((positionKey) => getSimplexUnitName([playerId, positionKey]));
    }

    _createRule(comboId, firstTeam, secondTeam) {
        const rules = [];

        const builder = RuleBuilder.fromAtMost(1);
        builder.addVars({
            [comboId]: -1,
            [firstTeam.keyUnitId]: 1,
            [secondTeam.keyUnitId]: 1,
        });
        rules.push(builder.createRule());

        const minPlayersQuantity = 2 * this.playersQuantity;

        const playersRuleBuilder = RuleBuilder.fromAtLeast(0);
        playersRuleBuilder.addVar(comboId, -1 * minPlayersQuantity);

        const playerIds = [...firstTeam.playerIds, ...secondTeam.playerIds];
        playerIds.map((pId) =>
            this._getSimplexUnitNamesByPlayerId(pId).map((simplexUnitName) => {
                playersRuleBuilder.addVar(simplexUnitName, 1);
            }),
        );

        rules.push(playersRuleBuilder.createRule());

        return rules;
    }

    createRules() {
        Object.values(this.combosMap).map((combo) => {
            Object.keys(combo).map((teamName) => {
                const teamTypes = combo[teamName];

                this.oneTeam.map((types) => {
                    const [type1, type2] = types;
                    if (!teamTypes.hasOwnProperty(type1) || !teamTypes.hasOwnProperty(type2)) {
                        return;
                    }
                    if (
                        this.playersQuantity > teamTypes[type1].playerIds.length ||
                        this.playersQuantity > teamTypes[type2].playerIds.length
                    ) {
                        return;
                    }

                    const comboId = Const.LP_CS_GAME_COMBINATION_PREFIX + [teamName, type1, teamName, type2].join('');

                    const rules = this._createRule(comboId, teamTypes[type1], teamTypes[type2]);

                    this.comboIds.push(comboId);
                    this.rules = [...this.rules, ...rules];
                    // console.log(comboId, {...rules});
                });
            });

            const [firstTeamName, secondTeamName] = Object.keys(combo);

            if (!firstTeamName || !secondTeamName) {
                return;
            }

            const firstTeamTypes = combo[firstTeamName];
            const secondTeamTypes = combo[secondTeamName];

            this.oppositeTeams.map((types) => {
                const [type1, type2] = types;
                if (!firstTeamTypes.hasOwnProperty(type1) || !secondTeamTypes.hasOwnProperty(type2)) {
                    return;
                }
                if (
                    this.playersQuantity > firstTeamTypes[type1].playerIds.length ||
                    this.playersQuantity > secondTeamTypes[type2].playerIds.length
                ) {
                    return;
                }

                const comboId =
                    Const.LP_CS_GAME_COMBINATION_PREFIX + [firstTeamName, type1, secondTeamName, type2].join('');

                const rules = this._createRule(comboId, firstTeamTypes[type1], secondTeamTypes[type2]);

                this.comboIds.push(comboId);
                this.rules = [...this.rules, ...rules];
                // console.log(comboId, {...rules});
            });

            this.oppositeTeams.map((types) => {
                const [type1, type2] = types;
                if (!firstTeamTypes.hasOwnProperty(type2) || !secondTeamTypes.hasOwnProperty(type1)) {
                    return;
                }
                if (
                    this.playersQuantity > firstTeamTypes[type2].playerIds.length ||
                    this.playersQuantity > secondTeamTypes[type1].playerIds.length
                ) {
                    return;
                }

                const comboId =
                    Const.LP_CS_GAME_COMBINATION_PREFIX + [firstTeamName, type2, secondTeamName, type1].join('');

                const rules = this._createRule(comboId, firstTeamTypes[type2], secondTeamTypes[type1]);

                this.comboIds.push(comboId);
                this.rules = [...this.rules, ...rules];
                // console.log(comboId, {...rules});
            });
        });
    }

    getRules() {
        return this.rules;
    }

    getComboIds() {
        return this.comboIds;
    }
}

export default DoubleSecondaryStackCompatibleTypesRulesBuilder;
